import {
  put,
  takeLeading,
  call,
  takeLatest,
  takeEvery,
  debounce,
} from "redux-saga/effects";
import request from "utils/api";
import _pick from "lodash/pick";
import _set from "lodash/set";
import { safe } from "providers/GeneralProvider/saga";
import {
  getConversationListRequest,
  getConversationListSuccess,
  getMessageListRequest,
  getMessageListSuccess,
  stopLoading,
  sendMessageRequest,
  sendMessageSuccess,
  createConversationRequest,
  createConversationSuccess,
  getConversationDetailRequest,
  getConversationDetailSuccess,
  seenConversationRequest,
  seenConversationSuccess,
  createTagRequest,
  getFonehouseTagsRequest,
  getFonehouseTagsSuccess,
  getConsulateTagsRequest,
  getConsulateTagsSuccess,
  getViegoTagsRequest,
  getViegoTagsSuccess,
  removeTagRequest,
  removeTagSuccess,
  removeTagFailure,
  getUnreadConversationsRequest,
  getSeenButNotRepliedRequest,
  getConversationStatsRequest,
  getConversationStatsSuccess,
  updateMessage,
  finishSendMessage,
  fetchProductPreviewRequest,
  fetchProductPreviewSuccess,
  fetchProductPreviewFailure,
} from "providers/MessengerProvider/slice";

function* handleGetConversationList(action) {
  const params = _pick(action.payload, ["limit", "type", "role", "page", "unreadOnly"]);
  let endpoint = "api/chat/conversations";

  _set(params, "role", "fonehouse");

  // Build query parameters
  const queryParams = [];
  if (action.payload?.tags) {
    const tag = action.payload?.tags.split("-")[0];
    queryParams.push(`tags[]=${tag}`);
  }
  if (action.payload?.unreadOnly !== undefined) {
    queryParams.push(`unreadOnly=${action.payload.unreadOnly}`);
  }

  if (queryParams.length > 0) {
    endpoint += `?${queryParams.join('&')}`;
  }

  const { data } = yield call(request, endpoint, params, {
    method: "GET",
  });

  yield put(
    getConversationListSuccess({ list: data.docs, page: action.payload.page, hasNextPage: data.hasNextPage })
  );
}

function* handleGetUnreadConversations(action) {
  const params = _pick(action.payload, ["limit", "type", "role", "page"]);
  let endpoint = "api/chat/conversations?unreadOnly=true";

  _set(params, "role", "fonehouse");

  if (action.payload?.tags) {
    const tag = action.payload?.tags.split("-")[0];
    endpoint += `&tags[]=${tag}`;
  }

  const { data } = yield call(request, endpoint, params, {
    method: "GET",
  });

  yield put(
    getConversationListSuccess({ list: data.docs, page: action.payload.page, hasNextPage: data.hasNextPage })
  );
}

function* handleGetSeenButNotRepliedConversations(action) {
  const params = _pick(action.payload, ["limit", "type", "role", "page"]);
  let endpoint = "api/chat/conversations?seenButNotReplied=true";

  _set(params, "role", "fonehouse");

  if (action.payload?.tags) {
    const tag = action.payload?.tags.split("-")[0];
    endpoint += `&tags[]=${tag}`;
  }

  const { data } = yield call(request, endpoint, params, {
    method: "GET",
  });

  yield put(
    getConversationListSuccess({ list: data.docs, page: action.payload.page, hasNextPage: data.hasNextPage })
  );
}

function* handleGetConversationStats(action) {
  const type = action.payload?.type || "FONEHOUSE_TO_USER";
  const endpoint = `api/chat/conversations/stats?role=fonehouse&type=${type}`;
  const { data } = yield call(request, endpoint, undefined, { method: "GET" });
  yield put(getConversationStatsSuccess(data));
}

function* handleGetMessageList(action) {
  const params = _pick(action.payload, [
    "limit",
    "time",
    "type",
    "conversationId",
  ]);
  const { data } = yield call(
    request,
    "api/chat-message/messages-admin",
    params,
    { method: "GET" }
  );

  yield put(
    getMessageListSuccess({
      list: data,
      time: action.payload.time,
      conversationId: params.conversationId,
    })
  );
}

function* handleSendMessage(action) {
  const { meta } = action;
  const { conversationId } = action.payload;
  try {
    const params = _pick(action.payload, [
      "text",
      "type",
      "conversationId",
      "attachments",
      "productId",
      "voucherId",
    ]);

    const { data } = yield call(
      request,
      "api/chat-message/message-admin",
      params,
      { method: "POST" }
    );

    // SIMPLE: Add message from API response directly to store
    yield put(updateMessage({ message: data }));

    yield put(seenConversationRequest({ conversationId }));
    yield put(sendMessageSuccess(data, meta));
    yield put(finishSendMessage());
  } catch (error) {
    yield put(finishSendMessage());
    throw error;
  }
}

function* handleCreateConversation(action) {
  const { meta } = action;
  const params = _pick(action.payload, ["receiverId"]);
  const { data } = yield call(
    request,
    "api/chat/conversation-for-admin",
    params,
    { method: "POST" }
  );
  yield put(createConversationSuccess(data, meta));
}

function* handleGetConversationDetail(action) {
  const params = _pick(action.payload, ["conversationId"]);
  if (params.conversationId) {
    const { data } = yield call(
      request,
      `api/chat/conversation/${action.payload.conversationId}`,
      undefined,
      {
        method: "GET",
      }
    );
    yield put(getConversationDetailSuccess({ detail: data }));
  }
}

function* handleSeenConversation(action) {
  const params = _pick(action.payload, ["conversationId"]);
  if (params.conversationId) {
    yield call(request, "api/chat-message/seen-message", params, {
      method: "POST",
    });
    yield put(seenConversationSuccess({ conversationId: params.conversationId }));
  }
}

function* handleCreateTag(action) {
  const { conversationId, name, color, conversationType } = action.payload;
  const tags = [
    {
      name,
      color,
    },
  ];
  const params = {
    tags,
  };
  // Use correct endpoint based on conversationType
  let endpoint;
  if (conversationType === "CONSULATE_TO_USER") {
    endpoint = `api/chat/conversations/${conversationId}/consulate-tags`;
  } else if (conversationType === "VIETGO_TO_USER") {
    endpoint = `api/chat/conversations/${conversationId}/viego-tags`;
  } else {
    endpoint = `api/chat/conversations/${conversationId}/tags`;
  }
  yield call(request, endpoint, params, { method: "POST" });
  const { data } = yield call(
    request,
    `api/chat/conversation/${conversationId}`,
    undefined,
    {
      method: "GET",
    }
  );
  yield put(getConversationDetailSuccess({ detail: data }));
  // Refresh the correct tags list
  if (conversationType === "CONSULATE_TO_USER") {
    yield put(getConsulateTagsRequest());
  } else if (conversationType === "VIETGO_TO_USER") {
    yield put(getViegoTagsRequest());
  } else {
    yield put(getFonehouseTagsRequest());
  }
}

function* handleGetFonehouseTags() {
  const endpoint = "api/chat/conversations/fonehouse-tags";
  const response = yield call(request, endpoint, undefined, { method: "GET" });
  yield put(getFonehouseTagsSuccess({ tags: response?.tags }));
}

function* handleGetConsulateTags() {
  const endpoint = "api/chat/conversations/consulate-tags";
  const response = yield call(request, endpoint, undefined, { method: "GET" });
  yield put(getConsulateTagsSuccess({ tags: response?.tags }));
}

function* handleGetViegoTags() {
  const endpoint = "api/chat/conversations/viego-tags";
  const response = yield call(request, endpoint, undefined, { method: "GET" });
  yield put(getViegoTagsSuccess({ tags: response?.tags }));
}

function* handleRemoveTag(action) {
  try {
    const { tagName, type = "FONEHOUSE_TO_USER" } = action.payload;
    const params = {
      tagName,
    };
    // Use correct endpoint based on type
    let endpoint;
    if (type === "CONSULATE_TO_USER") {
      endpoint = "api/chat/conversations/consulate-tags/remove";
    } else if (type === "VIETGO_TO_USER") {
      endpoint = "api/chat/conversations/viego-tags/remove";
    } else {
      endpoint = "api/chat/conversations/tags/remove";
    }

    // Make the API call
    yield call(request, endpoint, params, { method: "POST" });

    // Dispatch success action
    yield put(removeTagSuccess());

    // Refresh the conversation list to reflect removed tags
    yield put(
      getConversationListRequest({
        limit: 20,
        page: 1,
        type,
        role: "fonehouse",
      })
    );

    // Refresh the correct tags list
    if (type === "CONSULATE_TO_USER") {
      yield put(getConsulateTagsRequest());
    } else if (type === "VIETGO_TO_USER") {
      yield put(getViegoTagsRequest());
    } else {
      yield put(getFonehouseTagsRequest());
    }
  } catch (error) {
    console.error("Error removing tag:", error);
    // Dispatch failure action
    yield put(removeTagFailure());
  }
}
function* handleFetchProductPreview(action) {
  const { productId } = action.payload;
  try {
    const endpoint = `api/fonehouse/product-manager/product?idProduct=${productId}`;
    const { data } = yield call(request, endpoint, undefined, { method: "GET" });
    yield put(fetchProductPreviewSuccess(data));
  } catch (error) {
    yield put(fetchProductPreviewFailure({ error: "Không tìm thấy sản phẩm" }));
  }
}

function* onError() {
  yield put(stopLoading());
}

export default function* watchAuth() {
  // takeLatest để đảm bảo luôn lấy data mới nhất khi refresh
  // Tránh race condition: request cũ trả data stale ghi đè lên data mới
  yield takeLatest(
    getConversationListRequest.type,
    safe(handleGetConversationList, onError)
  );
  yield takeLatest(
    getUnreadConversationsRequest.type,
    safe(handleGetUnreadConversations, onError)
  );
  yield takeLatest(
    getSeenButNotRepliedRequest.type,
    safe(handleGetSeenButNotRepliedConversations, onError)
  );
  yield takeLatest(
    getMessageListRequest.type,
    safe(handleGetMessageList, onError)
  );
  yield takeLeading(sendMessageRequest.type, safe(handleSendMessage, onError));
  yield takeLeading(
    createConversationRequest.type,
    safe(handleCreateConversation, onError)
  );
  yield takeEvery(
    getConversationDetailRequest.type,
    safe(handleGetConversationDetail, onError)
  );
  yield takeEvery(
    seenConversationRequest.type,
    safe(handleSeenConversation, onError)
  );
  yield takeLatest(createTagRequest.type, safe(handleCreateTag, onError));
  yield takeLatest(
    getFonehouseTagsRequest.type,
    safe(handleGetFonehouseTags, onError)
  );
  yield takeLatest(
    getConsulateTagsRequest.type,
    safe(handleGetConsulateTags, onError)
  );
  yield takeLatest(
    getViegoTagsRequest.type,
    safe(handleGetViegoTags, onError)
  );
  yield takeLatest(removeTagRequest.type, safe(handleRemoveTag, onError));
  yield takeLatest(
    getConversationStatsRequest.type,
    safe(handleGetConversationStats, onError)
  );
  // Product preview with debounce (500ms) to avoid spamming API while typing
  yield debounce(
    500,
    fetchProductPreviewRequest.type,
    safe(handleFetchProductPreview, onError)
  );
}
